import { useMemo } from 'react';

import { BreadCrumb as PrimeBreadCrumb } from 'primereact/breadcrumb';
import { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import usePageTitle from '@/components/hooks/usePageTitle';

import style from './BreadCrumbNav.module.css';

type BreadCrumbNavProps = {
  className?: string;
};

export const BreadCrumbNav = ({ className }: BreadCrumbNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pageTitle } = usePageTitle();
  const items = useMemo<MenuItem[]>(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const searchParams = location.search;

    if (pathSegments.length === 0) {
      return [];
    }

    const firstSegment = pathSegments[0];
    const translationKey = firstSegment.replace(/-/g, ' ');
    const translated = t(translationKey);
    const translatedText =
      typeof translated === 'string' ? translated : translationKey;

    const documentTitle = translatedText
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    if (!documentTitle.includes('Fresh Shipment')) {
      document.title = documentTitle;
    }

    const breadcrumbItems = pathSegments.map((segment, index, arr) => {
      const isLast = index === arr.length - 1;
      const url = `/${pathSegments.slice(0, index + 1).join('/')}${searchParams}`;

      let label: string;
      if (isLast && pageTitle) {
        const translatedPageTitle = t(pageTitle);
        label =
          typeof translatedPageTitle === 'string'
            ? translatedPageTitle
            : pageTitle;
      } else {
        const segmentKey = segment.replace(/-/g, ' ');
        const translatedSegment = t(segmentKey);
        label =
          typeof translatedSegment === 'string'
            ? translatedSegment
            : segmentKey;
      }

      return {
        label,
        command: undefined,
        template: (item: MenuItem) => (
          <Link to={url} className="p-menuitem-link">
            {item.label}
          </Link>
        ),
      };
    });

    return breadcrumbItems;
  }, [location.pathname, location.search, pageTitle, t]);

  const home: MenuItem = {
    icon: 'pi pi-home',
    template: (item) => (
      <Link to="/" className="p-menuitem-link">
        {item.icon && <span className={item.icon}></span>}
      </Link>
    ),
  };

  return (
    <PrimeBreadCrumb
      className={`${style['bread-crumb-nav']} ${className}`.trim()}
      model={items}
      home={home}
    />
  );
};
